import { Trans } from "@lingui/macro";
import { Cog, Users, User, Menu as MenuIcon } from "lucide-react";
import Page from "../../const/Page";
import { useAppUIContext } from "../../context/AppUIContext";
import { Soribot } from "../../models/Soribot";
import { Workspace } from "../../models/Workspace";
import ProfileImg from "../components/ProfileImg";
import SoribotCreateModal from "./SoribotCreateModal";
import { useHistory } from "../../hooks/useRouterCompat";
import { Button } from "../../components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Link } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { MyNotifs } from "../../services/MyNotifs";
import { useState, useEffect } from "react";
import { cn } from "../../lib/utils";

export const MySoribotsPage: React.FC = () => {
  const { soribots, workspaces, setIsMobileMenuOpen } = useAppUIContext();
  const history = useHistory();
  const [myNotifCount, setMyNotifCount] = useState(0);

  useEffect(() => {
    setMyNotifCount(MyNotifs.NrOfNew);
    const interval = setInterval(() => {
      setMyNotifCount(MyNotifs.NrOfNew);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background md:rounded-panel md:border md:border-border">
      <header className="bg-background border-b px-2 md:px-6 py-2 md:py-4 flex items-center justify-between shrink-0 z-10">
        <div className="flex items-center gap-2 md:gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden relative"
            onClick={() => setIsMobileMenuOpen(true)}
            data-cy='menu'
          >
            <MenuIcon className="w-6 h-6" />
            {myNotifCount > 0 && (
              <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center bg-destructive hover:bg-destructive/90 text-meta-sm">
                {myNotifCount > 9 ? '9+' : myNotifCount}
              </Badge>
            )}
          </Button>
          <h1 className="text-lg md:text-xl font-bold"><Trans>Soribots</Trans></h1>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto w-full max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            <Trans>Soribots created by you or your channel members</Trans>
          </p>
          <SoribotCreateModal workspaceId={null} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {soribots.sort((a, b) => a.name?.localeCompare(b.name)).map(bot => BotItem(bot))}
        </div>
      </main>
    </div>
  );

  function BotItem(bot: Soribot) {
    const ws = workspaces.find(ws => ws.id === bot.workspaceId);
    return SoribotItem(bot, ws);
  }
}

export function SoribotItem(bot: Soribot, ws: Workspace | undefined) {
  return (
    <Card key={bot.id} className="hover:shadow-md transition-shadow relative overflow-hidden group">
      <Link to={`${Page.soribot}/details/${bot.id}`} className="absolute top-2 right-2 p-2 text-muted-foreground hover:text-foreground z-10">
        <Cog className="w-5 h-5" />
      </Link>

      <Link to={`${Page.soribot}/freechat/${bot.id}`} className="block">
        <div className="flex items-center p-4 gap-4">
          <div className="shrink-0">
            <div className="w-12 h-12 rounded-full overflow-hidden border">
              <ProfileImg url={bot.imageUrl ?? `/assets/img/ai-soribot.png`} className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg truncate pr-6">{bot.name}</h3>
            <div className="flex flex-col text-xs text-muted-foreground mt-1 gap-0.5">
              <div className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                <span className="truncate">{ws?.name ?? bot.workspaceId}</span>
              </div>
              <div className="flex items-center gap-1">
                <User className="w-3 h-3" />
                <span className="truncate">{bot.owner.name}</span>
              </div>
            </div>
          </div>
        </div>
      </Link>

      <div className="px-4 pb-4">
        <div className="flex flex-wrap gap-1 mb-2">
          {bot.rooms.map(r => (
            <span key={r.id} className="text-meta-sm bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded-sm truncate max-w-[150px]">
              # {r.name}
            </span>
          ))}
        </div>
        <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5em]">
          {bot.description ? bot.description : <Trans>No description</Trans>}
        </p>
      </div>
    </Card>
  );
}
export default MySoribotsPage;